/**
 * Which of an account's Cloud Workspaces this device uses.
 *
 * The account lists every workspace it holds, from any device. This device lists only the ones it
 * added — by creating or connecting one — so removing one here means it stays removed rather than
 * returning on the next listing. Kept per device and per account in Client Settings, beside the
 * other saved routes.
 */
import type { ClientSettings, EnvironmentId } from "@afar-research/contracts";

import { persistClientSettingsUpdate } from "../clientSettingsStore";

/** The key an account's choices are kept under; an account Connect did not name shares one. */
export function connectAccountKey(activeAccountId: string | undefined): string {
  return activeAccountId ?? "account";
}

/** Undefined means this device has not read the account's listing yet; see `seedConnectEnvironments`. */
export function addedConnectEnvironments(
  settings: Pick<ClientSettings, "connectEnvironmentIdsByAccount">,
  accountKey: string,
): ReadonlyArray<EnvironmentId> | undefined {
  return settings.connectEnvironmentIdsByAccount[accountKey];
}

export function isConnectEnvironmentAdded(
  added: ReadonlyArray<string> | undefined,
  environmentId: string,
): boolean {
  return added === undefined || added.some((id) => id === environmentId);
}

const update = (
  accountKey: string,
  change: (current: EnvironmentId[] | undefined) => EnvironmentId[],
) =>
  persistClientSettingsUpdate((settings) => ({
    ...settings,
    connectEnvironmentIdsByAccount: {
      ...settings.connectEnvironmentIdsByAccount,
      [accountKey]: change(settings.connectEnvironmentIdsByAccount[accountKey]?.slice()),
    },
  }));

export async function addConnectEnvironment(
  accountKey: string,
  environmentId: EnvironmentId,
): Promise<void> {
  // Connecting one this device already uses again leaves its place in the list alone.
  await update(accountKey, (current) =>
    current?.includes(environmentId) ? current : [...(current ?? []), environmentId],
  );
}

export async function removeConnectEnvironment(
  accountKey: string,
  environmentId: EnvironmentId,
): Promise<void> {
  await update(accountKey, (current) => (current ?? []).filter((id) => id !== environmentId));
}

/**
 * An account's first listing on this device decides what it already uses: every workspace listed
 * then. Workspaces created afterwards from another device are offered in the Add environment
 * dialog rather than appearing unasked.
 */
export async function seedConnectEnvironments(
  accountKey: string,
  environmentIds: ReadonlyArray<EnvironmentId>,
): Promise<void> {
  await update(accountKey, (current) => current ?? [...environmentIds]);
}

/**
 * Asks the Connect rows of the Environments list to read the account's workspaces again.
 *
 * Creating or connecting a workspace happens in the Add environment dialog, apart from those rows,
 * so the dialog signals here and the listing refreshes rather than waiting for its next poll.
 */
const refreshListeners = new Set<() => void>();

export function requestConnectEnvironmentsRefresh(): void {
  for (const listener of refreshListeners) listener();
}

export function subscribeConnectEnvironmentsRefresh(listener: () => void): () => void {
  refreshListeners.add(listener);
  return () => {
    refreshListeners.delete(listener);
  };
}
